import React, { useState } from 'react';
import {
  Reply, ReplyAll, Forward, Archive, Trash2,
  Inbox, CalendarPlus, Columns3, ThumbsDown, Flag,
} from 'lucide-react';
import { ICON_PX, ActionPill, Tooltip, TooltipTrigger, TooltipContent } from '../../../ui';
import { DomainBadge } from '../shared/DomainBadge';
import { UrgencyIndicator } from '../shared/UrgencyIndicator';
import { ConfidenceBar } from '../shared/ConfidenceBar';
import { ReclassifyDropdown } from '../shared/ReclassifyDropdown';
import { useInbox } from '../InboxContext';

// ── Mini calendar for schedule popover ───────────────────────────────────────
const MC_DAYS = ['S','M','T','W','T','F','S'];
function MiniCal({ value, onChange }) {
  const todayObj = new Date();
  const init = () => {
    if (value) { const [y, m] = value.split('-').map(Number); return new Date(y, m - 1, 1); }
    return new Date(todayObj.getFullYear(), todayObj.getMonth(), 1);
  };
  const [view, setView] = useState(init);
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const dim = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  const toStr = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>‹</button>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-deep)' }}>{view.toLocaleString('en-US', { month: 'long' })} {year}</span>
        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
        {MC_DAYS.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>)}
        {cells.map((d, i) => d == null
          ? <div key={`e${i}`} />
          : <button key={d} type="button" onClick={() => onChange(toStr(d))} style={{
              textAlign: 'center', fontSize: 11, padding: '4px 0', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: value === toStr(d) ? 'var(--aqua)' : 'transparent',
              color: value === toStr(d) ? 'white' : 'var(--navy-deep)', fontWeight: value === toStr(d) ? 700 : 400,
            }}>{d}</button>
        )}
      </div>
    </div>
  );
}

// ── Priority colors ──────────────────────────────────────────────────────────
const PRIORITY_COLOR = {
  critical: { border: 'var(--red-critical)', text: 'var(--red-critical)' },
  high:     { border: 'var(--red-critical)', text: 'var(--red-critical)' },
  medium:   { border: 'var(--yellow)',       text: 'var(--yellow)' },
  low:      { border: 'var(--green)',        text: 'var(--green)' },
};

// ── Popover wrapper (absolute, drops below trigger) ──────────────────────────
const POP_STYLE = {
  position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
  marginTop: 6, zIndex: 1050,
  background: 'var(--white)', borderRadius: 9,
  boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)',
};

function SchedulePopover({ schedDate, setSchedDate, onConfirm, onCancel }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={onCancel} />
      <div style={{ ...POP_STYLE, padding: 12, minWidth: 250 }}>
        <MiniCal value={schedDate} onChange={setSchedDate} />
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button type="button" onClick={onConfirm}
            style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
              background: schedDate ? 'var(--aqua)' : 'var(--surface)',
              color: schedDate ? 'white' : 'var(--text-muted)', cursor: schedDate ? 'pointer' : 'default' }}>Confirm</button>
          <button type="button" onClick={onCancel}
            style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function BoardPopover({ onSelect, onCancel }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={onCancel} />
      <div style={{ ...POP_STYLE, padding: 10, minWidth: 150 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy-deep)', marginBottom: 6, paddingLeft: 2 }}>Add to swimlane</div>
        {[
          { key: 'today',     label: 'Today',       color: 'var(--red-critical)' },
          { key: 'scheduled', label: 'Scheduled',   color: 'var(--yellow)' },
          { key: 'parking',   label: 'Parking Lot', color: 'var(--text-muted)' },
        ].map(({ key, label, color }) => (
          <button key={key} type="button" onClick={() => onSelect(key)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 5, border: 'none',
              fontSize: 12, fontWeight: 600, color, cursor: 'pointer', background: 'transparent', marginBottom: 1 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-warm)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >{label}</button>
        ))}
      </div>
    </>
  );
}

function FlagPopover({ flagPriority, setFlagPriority, flagFeedback, setFlagFeedback, onSubmit, onCancel }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={onCancel} />
      <div style={{ ...POP_STYLE, padding: 14, minWidth: 240 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-deep)', marginBottom: 4 }}>Flag Priority Issue</div>
        <div style={{ fontSize: 10, color: 'var(--text-dark-secondary)', marginBottom: 8 }}>Suggest a corrected priority — recalibrates the model.</div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
          {['critical', 'high', 'medium', 'low'].map((p) => {
            const c = PRIORITY_COLOR[p] || PRIORITY_COLOR.low;
            const sel = flagPriority === p;
            return (
              <button key={p} type="button" onClick={() => setFlagPriority(p)}
                style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                  padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                  border: `1px solid ${c.border}`, background: sel ? c.border : 'transparent',
                  color: sel ? 'white' : c.text, transition: 'background 0.1s' }}>{p}</button>
            );
          })}
        </div>
        <textarea placeholder="Why? (optional)" value={flagFeedback} onChange={(e) => setFlagFeedback(e.target.value)} rows={2}
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 11, padding: '5px 7px', border: '1px solid var(--surface)', borderRadius: 5, resize: 'none', color: 'var(--navy-deep)', outline: 'none', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button type="button" onClick={onSubmit}
            style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
              background: flagPriority ? 'var(--orange-warn)' : 'var(--surface)',
              color: flagPriority ? 'white' : 'var(--text-muted)', cursor: flagPriority ? 'pointer' : 'default' }}>Submit</button>
          <button type="button" onClick={onCancel}
            style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function EmailDetailHeader({ email }) {
  const { actions, startReply } = useInbox();

  // Popover states
  const [showSchedule, setShowSchedule] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [showFlag, setShowFlag] = useState(false);
  const [schedDate, setSchedDate] = useState('');
  const [flagPriority, setFlagPriority] = useState('');
  const [flagFeedback, setFlagFeedback] = useState('');

  if (!email) return null;

  const closeAll = () => { setShowSchedule(false); setShowBoard(false); setShowFlag(false); };

  const openPop = (setter) => {
    closeAll();
    setter(true);
  };

  const confirmSchedule = () => {
    if (!schedDate) return;
    actions.scheduleEmail(email.id, schedDate);
    setShowSchedule(false);
  };

  const confirmBoard = (swimlane) => {
    actions.boardEmail(email.id, swimlane);
    setShowBoard(false);
  };

  const submitFlag = () => {
    if (!flagPriority) return;
    // In future: postSignalAction(email.id, 'flag', { suggestedPriority, feedback })
    setShowFlag(false);
    setFlagPriority('');
    setFlagFeedback('');
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* Subject */}
      <h2 className="text-[14px] font-semibold text-text-dark break-words leading-snug">
        {email.subject}
      </h2>

      {/* Badges row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <DomainBadge domain={email.primaryDomain} />
        {email.domains?.filter((d) => d.domain !== email.primaryDomain && d.confidence > 20).map((d) => (
          <DomainBadge key={d.domain} domain={d.domain} className="opacity-50" />
        ))}
        <UrgencyIndicator urgency={email.urgency} />
        <ConfidenceBar confidence={email.classificationConfidence} />
        <ReclassifyDropdown
          currentDomain={email.primaryDomain}
          onReclassify={(domain) => actions.reclassify(email.id, domain)}
        />
      </div>

      {/* Action toolbar */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Reply / Reply All / Forward */}
        <ActionPill>
          <ActionPill.Button tip="Reply" color="var(--royal)" onClick={() => startReply(email, 'reply')}>
            <Reply size={ICON_PX.md} />
          </ActionPill.Button>
          <ActionPill.Button tip="Reply All" color="var(--royal)" onClick={() => startReply(email, 'replyAll')}>
            <ReplyAll size={ICON_PX.md} />
          </ActionPill.Button>
          <ActionPill.Button tip="Forward" color="var(--royal)" onClick={() => startReply(email, 'forward')}>
            <Forward size={ICON_PX.md} />
          </ActionPill.Button>
        </ActionPill>

        {/* Queue / Schedule / Board — each with relative wrapper for popovers */}
        <ActionPill>
          <ActionPill.Button
            tip="Queue it"
            color="var(--aqua)"
            onClick={() => actions.queueEmail(email.id)}
            disabled={email.queueStatus === 'queued'}
          >
            <Inbox size={ICON_PX.md} />
          </ActionPill.Button>
          <span style={{ position: 'relative' }}>
            <ActionPill.Button tip="Schedule it" color="var(--green)" onClick={() => openPop(setShowSchedule)}>
              <CalendarPlus size={ICON_PX.md} />
            </ActionPill.Button>
            {showSchedule && <SchedulePopover schedDate={schedDate} setSchedDate={setSchedDate} onConfirm={confirmSchedule} onCancel={() => setShowSchedule(false)} />}
          </span>
          <span style={{ position: 'relative' }}>
            <ActionPill.Button tip="Add to Board" color="var(--royal)" onClick={() => openPop(setShowBoard)}>
              <Columns3 size={ICON_PX.md} />
            </ActionPill.Button>
            {showBoard && <BoardPopover onSelect={confirmBoard} onCancel={() => setShowBoard(false)} />}
          </span>
        </ActionPill>

        {/* Dismiss / Flag Priority */}
        <ActionPill>
          <ActionPill.Button tip="Not important" color="var(--text-muted)" onClick={() => actions.dismissEmail(email.id)}>
            <ThumbsDown size={ICON_PX.md} />
          </ActionPill.Button>
          <span style={{ position: 'relative' }}>
            <ActionPill.Button tip="Change Priority" color="var(--orange-warn)" onClick={() => openPop(setShowFlag)}>
              <Flag size={ICON_PX.md} />
            </ActionPill.Button>
            {showFlag && <FlagPopover flagPriority={flagPriority} setFlagPriority={setFlagPriority} flagFeedback={flagFeedback} setFlagFeedback={setFlagFeedback} onSubmit={submitFlag} onCancel={() => setShowFlag(false)} />}
          </span>
        </ActionPill>

        {/* Archive / Delete */}
        <ActionPill>
          <ActionPill.Button tip="Archive" color="var(--green)" onClick={() => actions.archiveEmail(email.id)}>
            <Archive size={ICON_PX.md} />
          </ActionPill.Button>
          <ActionPill.Button tip="Delete" color="var(--red-critical)" onClick={() => actions.trashEmail(email.id)}>
            <Trash2 size={ICON_PX.md} />
          </ActionPill.Button>
        </ActionPill>

        {/* Flag (MS365 style) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => actions.toggleStar(email.id)}
              className="flex items-center p-1 rounded hover:bg-surface-warm transition-colors"
            >
              <Flag
                size={ICON_PX.md}
                style={{ color: email.isStarred ? 'var(--red-critical)' : 'var(--text-muted)' }}
                fill={email.isStarred ? 'var(--red-critical)' : 'none'}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{email.isStarred ? 'Unflag' : 'Flag'}</TooltipContent>
        </Tooltip>

        {/* Popovers are rendered inline inside the relative spans above */}
      </div>
    </div>
  );
}

export { EmailDetailHeader };
